// src/components/ListaJogadores.js
import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import "./ListaJogadores.css";

const strip = (s = "") =>
  s.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const nomeJog = (j) =>
  j.display_name || j.player_display_name || j.apelido || j.nome || j?.jogadores?.apelido || "Sem nome";

const ListaJogadores = ({ peladaId: groupId, onStartLoading, onFinishLoading }) => {
  const [session, setSession] = useState(null);

  const [confirmadosLinha, setConfirmadosLinha] = useState([]);
  const [confirmadosGoleiro, setConfirmadosGoleiro] = useState([]);
  const [membros, setMembros] = useState([]);

  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // busca somente em “Jogadores do Grupo”
  const [query, setQuery] = useState("");

  const headersAuth = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const exibirMensagem = (texto) => {
    setMsg(texto);
    setTimeout(() => setMsg(""), 2200);
  };

  // Carrega sessão + presenças + membros (efeito só depende de groupId)
  useEffect(() => {
    const load = async () => {
      if (!groupId) return;
      onStartLoading?.();
      setError("");
      try {
        const { data: s } = await api.get(`/api/groups/${groupId}/next-session`, {
          headers: headersAuth(),
        });
        setSession(s || null);

        if (!s?.id) {
          setConfirmadosLinha([]);
          setConfirmadosGoleiro([]);
          setMembros([]);
          return;
        }

        const { data: att } = await api.get(`/api/sessions/${s.id}/attendances`, {
          headers: headersAuth(),
        });

        setConfirmadosLinha(Array.isArray(att?.confirmados) ? att.confirmados : []);
        setConfirmadosGoleiro(Array.isArray(att?.goleirosConfirmados) ? att.goleirosConfirmados : []);

        const { data: mem } = await api.get(`/api/groups/${groupId}/members`, {
          headers: headersAuth(),
        });
        setMembros(Array.isArray(mem) ? mem : []);
      } catch (err) {
        const status = err?.response?.status;
        const m = err?.response?.data?.error || err?.message || "Erro ao carregar lista.";
        console.error("[ListaJogadores] erro:", status, m);
        setError(`(${status || "—"}) ${m}`);
      } finally {
        onFinishLoading?.();
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // Utilitário para recarregar após ações
  const recarregar = async () => {
    if (!groupId) return;
    const { data: s } = await api.get(`/api/groups/${groupId}/next-session`, { headers: headersAuth() });
    setSession(s || null);
    if (s?.id) {
      const { data: att } = await api.get(`/api/sessions/${s.id}/attendances`, { headers: headersAuth() });
      setConfirmadosLinha(att?.confirmados || []);
      setConfirmadosGoleiro(att?.goleirosConfirmados || []);
      const { data: mem } = await api.get(`/api/groups/${groupId}/members`, { headers: headersAuth() });
      setMembros(Array.isArray(mem) ? mem : []);
    }
  };

  // Ações
  const handleConfirmar = async (playerId) => {
    if (!session?.id) return;
    setLoading(true);
    try {
      await api.post(
        `/api/sessions/${session.id}/attendances/confirm`,
        { playerId },
        { headers: { ...headersAuth(), "Content-Type": "application/json" } }
      );
      exibirMensagem("Jogador confirmado!");
      await recarregar();
    } catch (err) {
      alert(err?.response?.data?.error || "Erro ao confirmar jogador.");
    } finally {
      setLoading(false);
    }
  };

  const handleDesistir = async (playerId) => {
    if (!session?.id) return;
    setLoading(true);
    try {
      await api.post(
        `/api/sessions/${session.id}/attendances/cancel`,
        { playerId },
        { headers: { ...headersAuth(), "Content-Type": "application/json" } }
      );
      exibirMensagem("Jogador removido dos confirmados.");
      await recarregar();
    } catch (err) {
      alert(err?.response?.data?.error || "Erro ao remover jogador.");
    } finally {
      setLoading(false);
    }
  };

  const handleMudarTipo = async (playerId, novoTipo) => {
    if (!session?.id) return;
    setLoading(true);
    try {
      await api.put(
        `/api/sessions/${session.id}/attendances/${playerId}`,
        { type: novoTipo }, // "linha" | "goleiro"
        { headers: { ...headersAuth(), "Content-Type": "application/json" } }
      );
      exibirMensagem("Tipo atualizado!");
      await recarregar();
    } catch (err) {
      alert(err?.response?.data?.error || "Erro ao mudar tipo.");
    } finally {
      setLoading(false);
    }
  };

  const handlePagamento = async (playerId) => {
    if (!session?.id) return;
    setLoading(true);
    try {
      await api.post(
        `/api/sessions/${session.id}/payments`,
        { playerId },
        { headers: { ...headersAuth(), "Content-Type": "application/json" } }
      );
      exibirMensagem("Pagamento registrado.");
      await recarregar();
    } catch (err) {
      alert(err?.response?.data?.error || "Erro ao registrar pagamento.");
    } finally {
      setLoading(false);
    }
  };

  const handleLimparLista = async () => {
    if (!session?.id) return;
    setLoading(true);
    try {
      const all = [...confirmadosGoleiro, ...confirmadosLinha];
      for (const a of all) {
        await api.post(
          `/api/sessions/${session.id}/attendances/cancel`,
          { playerId: a.player_id, silencioso: true },
          { headers: { ...headersAuth(), "Content-Type": "application/json" } }
        );
      }
      exibirMensagem("Lista de confirmados limpa.");
      await recarregar();
    } catch {
      alert("Erro ao limpar lista.");
    } finally {
      setLoading(false);
    }
  };

  // Derivados
  const idsConfirmados = useMemo(
    () => new Set([...confirmadosGoleiro, ...confirmadosLinha].map((c) => c.player_id)),
    [confirmadosGoleiro, confirmadosLinha]
  );
  const candidatos = useMemo(
    () => (membros || []).filter((m) => !idsConfirmados.has(m.player_id || m.id)),
    [membros, idsConfirmados]
  );

  // Busca apenas para candidatos
  const q = strip(query);
  const candFiltrados = useMemo(() => {
    if (!q) return candidatos;
    return candidatos.filter((p) => {
      const n = nomeJog(p);
      const alt = p.wa_jid || p.player_id || p.id || "";
      return strip(n).includes(q) || strip(alt).includes(q);
    });
  }, [candidatos, q]);

  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="lj">
      {msg && <div className="lj-toast">{msg}</div>}
      {loading && <div className="lj-inline-loader">⏳ Aguarde…</div>}

      <div className="lj-grid lj-grid--two">
        {/* CONFIRMADOS — GOLEIROS */}
        <section className="card">
          <header className="card-head">
            <h4>Confirmados — Goleiros</h4>
            <span className="badge-count">{confirmadosGoleiro.length}</span>
          </header>

          {confirmadosGoleiro.length ? (
            <ul className="list">
              {confirmadosGoleiro.map((j) => {
                const nome = nomeJog(j);
                const pago = j.paid || j.pg;
                return (
                  <li key={`gk-${j.player_id}`} className="pd-item">
                    {/* Nome isolado no topo */}
                    <div className="item-head">
                      <div className="pd-avatar">{nome.charAt(0).toUpperCase()}</div>
                      <div className="item-name" title={nome}>{nome}</div>
                    </div>

                    {/* Metadados (chips) em linha separada */}
                    <div className="item-meta">
                      <span className="chip">Goleiro</span>
                      {pago ? (
                        <span className="chip chip--ok">Pago</span>
                      ) : (
                        <span className="chip chip--warn">Pendente</span>
                      )}
                      {j.wa_jid && <span className="chip">{j.wa_jid}</span>}
                    </div>

                    {/* Ações */}
                    <div className="row-actions">
                      <button
                        className="btn-line"
                        onClick={() => handleMudarTipo(j.player_id, "linha")}
                      >
                        Mudar p/ linha
                      </button>
                      {!pago && (
                        <button
                          className="btn-line btn--primary-line"
                          onClick={() => handlePagamento(j.player_id)}
                        >
                          Marcar como pago
                        </button>
                      )}
                      <button
                        className="btn-line btn--danger-line"
                        onClick={() => handleDesistir(j.player_id)}
                      >
                        Remover da lista
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="empty">
              <div className="empty-emoji">🧤</div>
              <div className="empty-title">Nenhum goleiro</div>
              <div className="empty-sub">Confirme alguém como goleiro.</div>
            </div>
          )}
        </section>

        {/* CONFIRMADOS — LINHA */}
        <section className="card">
          <header className="card-head">
            <h4>Confirmados — Linha</h4>
            <span className="badge-count">{confirmadosLinha.length}</span>
          </header>

          {confirmadosLinha.length ? (
            <ul className="list">
              {confirmadosLinha.map((j) => {
                const nome = nomeJog(j);
                const pago = j.paid || j.pg;
                return (
                  <li key={`ln-${j.player_id}`} className="pd-item">
                    {/* Nome isolado no topo */}
                    <div className="item-head">
                      <div className="pd-avatar">{nome.charAt(0).toUpperCase()}</div>
                      <div className="item-name" title={nome}>{nome}</div>
                    </div>

                    {/* Metadados (chips) */}
                    <div className="item-meta">
                      <span className="chip">Linha</span>
                      {pago ? (
                        <span className="chip chip--ok">Pago</span>
                      ) : (
                        <span className="chip chip--warn">Pendente</span>
                      )}
                      {j.wa_jid && <span className="chip">{j.wa_jid}</span>}
                    </div>

                    {/* Ações */}
                    <div className="row-actions">
                      <button
                        className="btn-line"
                        onClick={() => handleMudarTipo(j.player_id, "goleiro")}
                      >
                        Mudar p/ goleiro
                      </button>
                      {!pago && (
                        <button
                          className="btn-line btn--primary-line"
                          onClick={() => handlePagamento(j.player_id)}
                        >
                          Marcar como pago
                        </button>
                      )}
                      <button
                        className="btn-line btn--danger-line"
                        onClick={() => handleDesistir(j.player_id)}
                      >
                        Remover da lista
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="empty">
              <div className="empty-emoji">⚽</div>
              <div className="empty-title">Sem jogadores de linha</div>
              <div className="empty-sub">Confirme alguém como linha.</div>
            </div>
          )}

          <div className="footer-actions">
            <button className="btn-line btn--danger-line" onClick={handleLimparLista}>
              🧹 Limpar lista de confirmados
            </button>
          </div>
        </section>

        {/* JOGADORES DO GRUPO (com busca somente aqui) */}
        <section className="card">
          <header className="card-head">
            <h4>Jogadores do Grupo</h4>
            <span className="badge-count">{candFiltrados.length}</span>
          </header>

          <label className="search search--inside">
            <span className="search-icon" aria-hidden>🔎</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome ou JID/telefone…"
              className="search-input"
              type="text"
              inputMode="search"
            />
            {query && (
              <button
                type="button"
                className="search-clear"
                onClick={() => setQuery("")}
                aria-label="Limpar busca"
                title="Limpar"
              >
                ×
              </button>
            )}
          </label>

          {candFiltrados.length ? (
            <ul className="list">
              {candFiltrados.map((m) => {
                const id = m.player_id || m.id;
                const nome = nomeJog(m);
                return (
                  <li key={`c-${id}`} className="pd-item">
                    {/* Nome isolado no topo */}
                    <div className="item-head">
                      <div className="pd-avatar">{nome.charAt(0).toUpperCase()}</div>
                      <div className="item-name" title={nome}>{nome}</div>
                    </div>

                    {/* Metadados (opcional: jid) */}
                    <div className="item-meta">
                      <span className="chip">Membro</span>
                      {m.wa_jid && <span className="chip">{m.wa_jid}</span>}
                    </div>

                    {/* Ações */}
                    <div className="row-actions">
                      <button className="btn-line btn--primary-line" onClick={() => handleConfirmar(id)}>
                        Confirmar
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="empty">
              <div className="empty-emoji">✅</div>
              <div className="empty-title">Sem candidatos</div>
              <div className="empty-sub">Todos já confirmados ou filtro muito restrito.</div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ListaJogadores;
